import { useState, useEffect } from 'react';

import { MenuList, Paper, Popper, Badge } from '@material-ui/core';
import { captureException } from '@sentry/react';
import { connect } from 'react-redux';
import { selectIsAuthenticated } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { HideScrollbarCSS } from 'styles/common';
import { throttle } from 'throttle-debounce';

import { NOTIFICATION_POLL_INTERVAL } from 'constants/ProjectConstants';
import useInterval from 'constants/useInterval';

import { getTextFromTextMacros } from 'services/helpers/post';
import { getNotificationsCountAndData } from 'services/notifications';

import { useNotifications } from './context';

// const UPDATE_NOTIFICATIONS_DATA = 'UPDATE';
// const ADD_TO_NOTIFICATIONS = 'ADD';
// const ADD_TO_NOTIFICATIONS_END = 'ADD_END';
// const UPDATE_NOTIFICATION_CLICK_STATUS = 'UPDATE_CLICKED';

// function notificationsReducer(state, action) {
//   switch (action.type) {
//     case UPDATE_NOTIFICATIONS_DATA: {
//       return action.data;
//     }
//     case ADD_TO_NOTIFICATIONS: {
//       // Adding new notifications to the front
//       const newState = [...action.data, ...state];
//       return newState;
//     }
//     case ADD_TO_NOTIFICATIONS_END: {
//       // Adding new notifications to the front
//       const newState = [...state, ...action.data];
//       return newState;
//     }
//     case UPDATE_NOTIFICATION_CLICK_STATUS: {
//       const newState = [...state];
//       const notification = newState.find(n => n.id === action.data.id);
//       notification.isClicked = true;
//       return newState;
//     }
//     default:
//       return state;
//   }
// }

// function metaReducer(state, action) {
//   switch (action.type) {
//     // case 'UPDATE_HAS_MORE':
//     //   return { ...state, hasMore: action.data.hasMore };
//     // case 'UPDATE_PAGE':
//     //   return { ...state, page: action.data.page };
//     case 'UPDATE_META':
//       return { ...state, ...action.data };
//     // case 'UPDATE_LOADING':
//     //   return { ...state, loading: action.data.loading };
//     default:
//       return state;
//   }
// }

const throttledCreateNotification = throttle(5000, (notificationData = {}) => {
  const { source } = notificationData;
  const text = source.text || getTextFromTextMacros(notificationData.text, notificationData.macros);
  if (!text) return;
  const notification = new Notification(text, {
    icon: source.image,
  });
});

function notify(data = []) {
  try {
    if (data && data.length < 1) return;
    function sendNotifications() {
      // Making sure that at a time we only show max 3 notifications
      data.slice(0, 3).forEach((notificationData, i) => {
        throttledCreateNotification(notificationData);
      });
    }

    if (!('Notification' in window)) {
      // alert('This browser does not support desktop notification');
    }

    // Let's check whether notification permissions have already been granted
    else if (Notification.permission === 'granted') {
      // If it's okay let's create a notification
      sendNotifications();
    }

    // Otherwise, we need to ask the user for permission
    else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((permission) => {
        // If the user accepts, let's create a notification
        if (permission === 'granted') {
          sendNotifications();
        }
      });
    }
  } catch (err) {
    console.error('Error occurred while notifying', err);
    captureException(err);
  }
}

function NotificationsWrapper({
  displayArrow = false,
  maxWidth = '22%',
  notificationStyles = {},
  showDetails,
  isAuthenticated,
  children,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  // const [notificationCount, setNotificationCount] = useState(0);
  // const [notificationsData, dispatchNotificationsUpdater] = useReducer(notificationsReducer, []);
  // const [latestNotificationId, setLatestNotificationId] = useState(null);
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const limit = 10;
  // const [meta, dispatchMeta] = useReducer(metaReducer, {
  //   hasMore: true,
  //   loading: true,
  //   error: null
  // });

  const {
    state,
    addToNotifications,
    updateNotifications,
    addToEndOfNotifications,
    updateNotificationClickedStatus,
    updateNotificationsMeta,
    setNotificationsCount,
    setLatestNotificationsId,
  } = useNotifications();

  const {
    notificationsCount: notificationCount,
    latestNotificationId,
    notificationsData,
    notificationsMeta: meta,
  } = state ?? {};

  const [pollingInterval, setPollingInterval] = useState(NOTIFICATION_POLL_INTERVAL);

  const onWindowFocus = () => {
    setIsWindowFocused(true);
  };

  const onWindowBlur = () => {
    setIsWindowFocused(false);
  };

  useEffect(() => {
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('blur', onWindowBlur);
    };
  });

  function pollNotifications() {
    if (isAuthenticated && latestNotificationId && isWindowFocused) {
      getNotificationsCountAndData({
        latestPullNotificationId: latestNotificationId,
        callType: 'POLLING',
      })
        .then((res) => {
          if (res.data && res.data.length > 0) {
            setLatestNotificationsId(res.data[0].id);
            // disabling browser notification. code is still in place.
            // notify(res.data);
            addToNotifications(res.data);
          }
          setNotificationsCount(res.count);
          // reset polling interval to default for successful calls.
          setPollingInterval(NOTIFICATION_POLL_INTERVAL);
        })
        .catch(() => {
          // keep on exponentially increasing polling interval in case of failures.
          setPollingInterval((prev) => 2 * prev);
        });
    }
  }

  useInterval(pollNotifications, pollingInterval);

  function updateMeta(newState) {
    // dispatchMeta({ type: 'UPDATE_META', data: newState });
    updateNotificationsMeta(newState);
  }
  // first set of notifications
  useEffect(() => {
    getNotificationsCountAndData({ callType: 'PAGINATION', limit })
      .then((res) => {
        if (res.data && res.data.length > 0) {
          setLatestNotificationsId(res.data[0].id);
          // dispatchNotificationsUpdater({ type: UPDATE_NOTIFICATIONS_DATA, data: res.data });
          updateNotifications(res.data);
        }
        if (res.data && res.data.length < limit) updateMeta({ hasMore: false });
        setNotificationsCount(res.count);
      })
      .catch((err) => {
        updateMeta({ hasMore: false, error: err.message });
      })
      .finally(() => updateMeta({ loading: false }));
  }, []);

  return children;

  // return (
  //   <NavItemWrapper active={anchorEl}>
  //     <DropdownContainer>
  //       <HeaderIconContainer onClick={handleClick}>
  //         <NotificationCountBadge size="small" badgeContent={notificationCount} color="error">
  //           <StyledIconButton style={notificationStyles}>
  //             <NotificationHeaderIcon size="headerSmall" color={anchorEl ? 'primary' : 'grey'} />
  //           </StyledIconButton>
  //         </NotificationCountBadge>
  //         <StyledTypography clickable variant="body2" color={anchorEl ? 'primary' : 'grey'}>
  //           <TextAndArrowIconContainer isSelected={anchorEl}>
  //             Notification{' '}
  //             {displayArrow && (
  //               <ArrowDownIcon
  //                 size="tiniest"
  //                 color={anchorEl ? 'primary' : 'grey'}
  //                 leftGutter={1}
  //                 onClick={handleClick}
  //               />
  //             )}
  //           </TextAndArrowIconContainer>
  //         </StyledTypography>
  //       </HeaderIconContainer>
  //       <StyledPopper
  //         maxWidth={maxWidth}
  //         open={Boolean(anchorEl)}
  //         anchorEl={anchorEl}
  //         role={undefined}
  //         transition
  //       >
  //         <ClickAwayListener onClickAway={handleClose}>
  //           <StyledPaper>
  //             {!meta.loading && notificationsData.length > 0 && (
  //               <InfiniteScroll
  //                 initialLoad={false}
  //                 pageStart={1}
  //                 hasMore={meta.hasMore}
  //                 loadMore={fetchPaginatedNotifications}
  //                 useWindow={false}
  //                 loader={<Skeleton key={`notificationLoader`} height="50px" />}
  //               >
  //                 <StyledMenuList disablePortal id={'notificationsSection'}>
  //                   {notificationsData.map((notificationData, index) => (
  //                     <NotificationComponent
  //                       key={`notificationsSection-item-${notificationsData.id || index}`}
  //                       updateIsClicked={updateNotificationClickStatus}
  //                       closePopup={handleClose}
  //                       data={notificationData}
  //                       showDetails={showDetails}
  //                     />
  //                   ))}
  //                 </StyledMenuList>
  //               </InfiniteScroll>
  //             )}
  //             {!meta.loading && notificationsData.length <= 0 && (
  //               <NoNotificationsView key="noNotificationView" />
  //             )}
  //             {meta.loading && <NoDataComponent message="Loading your notifications" />}
  //           </StyledPaper>
  //         </ClickAwayListener>
  //       </StyledPopper>
  //     </DropdownContainer>
  //   </NavItemWrapper>
  // );
}

const StyledPopper = styled(Popper)`
  max-width: ${(props) => props.maxWidth};
  li {
    white-space: inherit;
    display: block;
  }
  z-index: 9;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledPaper = styled(Paper)`
  margin-top: ${(props) => props.theme.spacing(3)}rem;
  padding: 0;
  // background: #fafafa;
  max-height: 50vh;
  overflow: scroll;
  ${HideScrollbarCSS};
`;

const StyledMenuList = styled(MenuList)`
  padding: 0;
  height: 100%;
  overflow: auto;
`;

const NotificationCountBadge = styled(Badge)`
  .MuiBadge-badge {
    background-color: ${(props) => props.theme.palette.red.main};
    top: 5px;
    right: -5px;
  }
`;

const mapStateToProps = (state, ownProps) => ({
  isAuthenticated: selectIsAuthenticated(state),
});

export default connect(mapStateToProps, null)(NotificationsWrapper);
